import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DeleteBySelectionModalComponent } from '../../delete-by-selection-modal/delete-by-selection-modal.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FinalService, StorageService } from '../../../services';
import { RemoveEmpty } from '../../../utils';
import { ToastService } from '../../../services/toast/toast.service';
import { AnalyticsService } from '@web-frontend/shared/services/analytics/analytics.service';
import AmplitudeEvents from 'src/types/amplitude.enum';
import { Phone } from '../../../interfaces';
import { FormatEmailValidatorAllowNull } from '../../../validators/email';
import { finalize } from 'rxjs/operators';
import { MapperPlaceResult } from '@web-frontend/shared/class/location';

@Component({
  selector: 'roma-create-final-partially',
  templateUrl: './create-final-partially.component.html',
  styleUrls: ['./create-final-partially.component.scss'],
})
export class CreateFinalPartiallyComponent implements OnInit {
  isLoading = false;
  showToolTip = false;
  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    nif: new FormControl(''),
    address: new FormControl(''),
    idCreatedBy: new FormControl(StorageService.UserId),
    email: new FormControl('', FormatEmailValidatorAllowNull()),
    phonesArr: new FormControl([]),
    emailArr: new FormControl([]),
  });
  isResponsive = window.innerWidth <= 768;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.isResponsive = window.innerWidth <= 768;
  }

  public data?: { id?: string; mode?: 'ADD' | 'EDIT' };

  constructor(
    private readonly dialogRef: MatDialogRef<CreateFinalPartiallyComponent>,
    private readonly dialog: MatDialog,
    private readonly i18n: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly finalService: FinalService,
    private readonly toastService: ToastService,
    private analyticsService: AnalyticsService,
    @Inject(MAT_DIALOG_DATA) data?: { id?: string; mode?: 'ADD' | 'EDIT' }
  ) {
    this.data = data;
  }

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .subscribe(async () => await this.onBackdropClick());

    if (this.data?.mode === 'EDIT' && this.data?.id) {
      await this.requestCustomer();
    }
  }

  get phone(): Phone {
    return this.form.value?.phonesArr[0] || { pre: '', number: '' };
  }

  close(): void {
    this.onBackdropClick().finally(() => (this.isLoading = false));
  }

  getTitle(): string {
    if (this.data?.mode === 'EDIT') {
      return this.i18n.instant('general.editFinal');
    } else {
      return this.i18n.instant('general.addFinal');
    }
  }

  private async requestCustomer() {
    try {
      const params = {
        'ids[]': [this.data.id],
      };

      const res = await this.finalService
        .search(params)
        .pipe(
          finalize(() => {
            this.draw();
          })
        )
        .toPromise();
      if (res.docs.length) {
        const [customer] = res.docs;
        this.form.patchValue({
          name: customer.name,
          nif: customer.nif,
          address: customer.address,
          email: customer.emailArr?.[0] || '',
          phonesArr: customer.phonesArr || [],
          idCreatedBy: undefined,
          emailArr: customer.emailArr || [],
        });
        this.cdRef.markForCheck();
      }

      this.draw();
    } catch (error) {}
  }

  private draw() {
    this.cdRef.detectChanges();
  }

  validateForm(): void {
    this.form.markAllAsTouched();
    this.draw();
  }

  async submit(): Promise<void> {
    this.isLoading = true;
    this.validateForm();
    const value = RemoveEmpty(this.form.value) as {
      name: string;
      nif?: string;
      address?: string;
      idCreatedBy?: string;
      email?: string;
      phonesArr?: Phone[];
      emailArr?: string[];
    };
    if (this.data?.mode === 'EDIT' && this.data?.id) {
      if (!value.emailArr) value.emailArr = [];
      if (value.emailArr?.length > 0) {
        value.emailArr[0] = value.email;
        value.email = undefined;
      } else {
        value.emailArr = [value.email || ''];
        value.email = undefined;
      }
      await this.finalService
        .updateOne(this.data.id, value)
        .toPromise()
        .then((res) => {
          this.toastService.show({
            text: this.i18n.instant('finals.update.success'),
            type: 'success',
            msDuration: 4000,
          });
          this.isLoading = false;
          this.dialogRef.close(res._id);
        });

      this.analyticsService.trackEvent({
        sources: ['amplitude'],
        eventName: AmplitudeEvents.final_update,
      });
    } else {
      if (value.email) {
        value.emailArr = [value.email];
        value.email = undefined;
      }
      await this.finalService
        .create(value)
        .toPromise()
        .then((res) => {
          this.toastService.show({
            text: this.i18n.instant('finals.create.success'),
            type: 'success',
            msDuration: 4000,
          });
          this.isLoading = false;
          this.dialogRef.close(res._id);
        });

      this.analyticsService.trackEvent({
        sources: ['amplitude'],
        eventName: AmplitudeEvents.final_create,
      });
    }
  }

  autocompleteSelected(result): void {
    const { formattedAddress } = MapperPlaceResult(result);
    this.form.patchValue({
      address: formattedAddress,
    });

    this.draw();
  }

  phoneChanges(event: Phone): void {
    const phones = this.form.value['phonesArr'];
    phones[0] = event;
    this.form.patchValue({
      phonesArr: phones,
    });
  }

  displayTooltip(canDisplay = true): void {
    if (this.isResponsive || !this.form.invalid) return;
    this.showToolTip = canDisplay;
  }

  changeName(event: InputEvent): void {
    const inputElement = event.target as HTMLInputElement;
    this.form.patchValue({
      name: inputElement.value,
    });
    this.validateForm();
    this.draw();
  }

  public async onBackdropClick(): Promise<void> {
    if (this.form.touched) {
      const dialogRef = this.dialog.open(DeleteBySelectionModalComponent, {
        panelClass: 'delete-by-selection-modal',
        data: {
          title: this.i18n.instant('general.withoutSave'),
          confirmLabel: this.i18n.instant('general.buttonExit'),
          showBody: false,
        },
      });

      dialogRef.afterClosed().subscribe((res) => {
        if (res == 'EXECUTE') {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }
}
